/**
 * Market Pop web client entry: services, DOM overlay, Phaser game, error boundary, deep
 * links, lifecycle and sync. The same entry runs in the Capacitor apps and in the Discord
 * Activity: each platform's own code is a lazy chunk (src/native, src/discord) that only that
 * platform loads (./platform.ts), before the services exist, so a web visitor downloads neither.
 */
import '@fontsource-variable/fredoka/wght.css';
import './ui/styles.css';
import * as Phaser from 'phaser';
import { GlobalErrorHandlers } from '@mp/adapters/crash';
// first: the platform chunk (native, Discord) is awaited there, before the services exist
import { bootFailed, discord, e2e, kv, native, params, platform } from './platform.js';
import type { RemoteConfig } from './config/content.js';
import { FEATURES, handleLink, openView } from './features/registry.js';
import { Art } from './game/art.js';
import { setApp, type AppContext } from './game/context.js';
import { audioLoader, levelSources } from './game/sources.js';
import { installDprScale } from './game/viewport.js';
import { loadLocale, matchLocale, t } from './i18n/index.js';
import { BootScene } from './scenes/BootScene.js';
import { MapScene } from './scenes/MapScene.js';
import { PlayScene } from './scenes/PlayScene.js';
import { ShopScene } from './scenes/ShopScene.js';
import { errorCode } from './services/api.js';
import { parseDeepLink } from './services/deeplinks.js';
import type { RoundBody } from './services/dto.js';
import { createServices, setSyncHooks } from './services/index.js';
import { webInstallReferrer } from './services/installReferrer.js';
import { op, settingsBody } from './services/ops.js';
import { rememberRemote, trackExposures } from './services/remoteConfig.js';
import { deviceClass } from './services/prompts.js';
import { offlineAmount } from './services/rewards.js';
import type { Settings } from './services/state.js';
import { webProviders } from './services/webProviders.js';
import { urlOverride } from './services/urlOverride.js';
import { installHooks } from './testing/hooks.js';
import { UiBridge } from './ui/bridge.js';
import { mountOverlay } from './ui/Overlay.js';
import { setUiSound } from './ui/sound.js';
import { setSpriteSource } from './ui/sprite.js';

const dev = import.meta.env.DEV;
const apiParam = params.get('api');
// ?api= points only at this device outside dev builds (services/urlOverride.ts): a link must not redirect tokens
const apiUrl = apiParam === 'off' ? '' : (urlOverride(apiParam, dev) ?? discord?.apiUrl ?? import.meta.env.VITE_API_URL ?? 'http://localhost:8787');
const link = native?.launch ?? parseDeepLink(location.href);
/** The web build's own provider config and selection; unused on Capacitor and Discord (2A). */
const web = webProviders(import.meta.env, location.origin, __APP_VERSION__);

let services: ReturnType<typeof createServices>;
try {
  services = createServices({
    kv,
    fetch: (...args) => fetch(...args),
    apiUrl,
    now: () => Date.now(),
    levelSources: levelSources(dev || e2e || params.get('devlevels') === '1'),
    audioLoader: audioLoader(),
    // VITE_MP_PROVIDERS wins where it is set (.env.capacitor, .env.discord, the DO web spec);
    // a plain web build otherwise takes what webProviders found config for (2A).
    providers: (import.meta.env.VITE_MP_PROVIDERS as string | undefined) ?? (native || discord ? '' : web.selection),
    search: location.search,
    adSeconds: e2e ? 1 : 5,
    release: __APP_VERSION__,
    // privacy region: CDN country in production; ?country= / ?tz= let dev and e2e pick one
    country: dev || e2e ? params.get('country') : null,
    ...(dev || e2e ? (params.get('tz') ? { timeZone: params.get('tz') } : e2e ? { timeZone: 'UTC' } : {}) : {}),
    generateDaily: (date) => import('./features/daily/generate.js').then((m) => m.generateDaily(date)),
    platform,
    // Capacitor and Discord: their entry chunk's providers. Web: H5 ads, Stripe checkout, web push
    // and the Google / Apple sign-in configs, each from its VITE_* value (services/webProviders.ts).
    platformProviders: native?.providers ?? discord?.providers ?? web.config,
    realtimeUrl: (dev || e2e ? urlOverride(params.get('rt'), dev) : null) ?? discord?.realtimeUrl ?? (import.meta.env.VITE_REALTIME_URL as string | undefined) ?? 'ws://localhost:8788/ws',
  });
} catch (e) {
  // a provider config createProviders refuses (a missing AdMob unit, Google without Apple on iOS…)
  bootFailed(e);
}

const ui = new UiBridge();
const store = services.store;

// SO-05: attribution rides along with guest auth (new players only); the link is handled after boot
if (link.attribution && !store.state.auth) store.update((s) => (s.attribution = link.attribution));
// where the install came from (Play's referrer on Android, the landing URL's utm_* on the web): kept until guest creation succeeds
const source = native?.installReferrer ?? (native || discord ? null : webInstallReferrer(location.search));
if (source && !store.state.auth && !store.state.installReferrer) store.update((s) => (s.installReferrer = source));
if (link.link || link.attribution) history.replaceState(null, '', link.cleanPath);
if (link.link || link.attribution) services.analytics.track('link_opened', { kind: link.link?.kind ?? 'attribution', ref: link.attribution?.ref ?? null, ...(native?.launchVia ? { via: native.launchVia } : {}) });

// first run: speak the device's language when we have it
if (!store.state.auth && Object.keys(store.state.levels).length === 0 && store.state.settings.locale === 'en' && !e2e) {
  const loc = matchLocale(navigator.languages ?? [navigator.language]);
  if (loc !== 'en') store.update((s) => (s.settings.locale = loc));
}

// Flow D: coins earned while away (accrued since the last collect), measured before this session
const bootNow = services.now();
const awayMs = Math.max(0, bootNow - store.state.lastSeenAt);
const session: AppContext['session'] = {
  offlineCoins: offlineAmount(store.state, bootNow),
  offlineHours: Math.min(8, Math.round((Math.max(awayMs, bootNow - store.state.lastCollectAt) / 3_600_000) * 10) / 10),
  offlineShown: false,
  dailyShown: false,
  deepLink: link.link,
  checked: new Set(),
  devOpen: devOpen(),
};

/** dev/e2e `?open=<viewKind>&props=<json>` (a feature screen right after boot). */
function devOpen(): AppContext['session']['devOpen'] {
  const kind = dev || e2e ? params.get('open') : null;
  if (!kind) return null;
  try {
    return { kind, props: JSON.parse(params.get('props') ?? '{}') as unknown };
  } catch {
    return { kind, props: {} };
  }
}

/** dev/e2e `?flags=teams,events,-ads`: force remote-config flags on (or off with `-`), over /v1/config. */
const forcedFlags: Record<string, boolean> = Object.fromEntries(
  (dev || e2e ? (params.get('flags') ?? '') : '')
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean)
    .map((f) => (f.startsWith('-') ? [f.slice(1), false] : [f, true])),
);
Object.assign(services.remote.flags, forcedFlags);
store.update((s) => (s.lastSeenAt = bootNow));
services.sync.releaseAll();

const art = new Art(() => ctx.game!.textures);
const ctx: AppContext = {
  services,
  art,
  ui,
  flags: { e2e, dev, skipAnimations: false },
  game: null,
  city: store.state.city,
  get remote() {
    return services.remote;
  },
  session,
  // the Activity lives inside the Discord app, like the native apps: no "get the app" prompt or QR code
  device: deviceClass(navigator.userAgent, matchMedia('(pointer: coarse)').matches, platform.native || platform.kind === 'discord'),
  changeSettings,
};
setApp(ctx);

// ---------------------------------------------------------------- error boundary + crash reports
let fatalShown = false;
function fatal(err: unknown): void {
  const message = err instanceof Error ? err.message : String(err);
  console.error('[market-pop]', err);
  services.analytics.track('error', { where: 'boundary', message });
  if (fatalShown) return;
  fatalShown = true;
  // the native launch splash would hide the failure
  void native?.ready();
  if (document.getElementById('splash') && !document.getElementById('splash')!.classList.contains('done')) {
    window.__mpBootFailed?.();
    return;
  }
  void ui.open('error', { message }).then(() => location.reload());
}
window.addEventListener('error', (e) => fatal(e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => fatal(e.reason));
if (services.api.enabled && !e2e) new GlobalErrorHandlers(services.providers.crash).install();
// the HTML splash is on screen: the native launch splash can go
void native?.ready();

// ---------------------------------------------------------------- sync → UI
setSyncHooks(services, {
  onRejected: (o, reason) => {
    services.analytics.track('error', { where: 'sync', op: o.kind, reason });
    if (reason === 'rare_item_required') ui.toast(t('restock.rejected'));
    // a refused win takes its stars and coins away at the next snapshot: say so rather than let them vanish
    else if (o.kind === 'round') {
      if ((o.body as RoundBody).claimed.result === 'win') ui.toast(t('sync.roundRejected'));
    } else if (o.kind !== 'settings') ui.toast(t('sync.rejected'));
  },
  onStatus: () => ui.refresh(),
  onReconciled: () => {
    ui.refresh();
    void services.privacy.pushAge();
    void services.daily.submit().catch(() => undefined);
  },
});

// ---------------------------------------------------------------- settings
function applySettings(s: Settings): void {
  services.audio.configure(s.sound, s.music);
  document.documentElement.classList.toggle('reduced-motion', s.reducedMotion);
}

function changeSettings(patch: Partial<Settings>): void {
  const before = store.state.settings;
  store.update((s) => (s.settings = { ...s.settings, ...patch }));
  const after = store.state.settings;
  applySettings(after);
  for (const [k, v] of Object.entries(patch)) services.analytics.track('settings_change', { key: k, value: v as boolean | string });
  if (patch.colorBlind !== undefined && patch.colorBlind !== before.colorBlind) {
    void art.setColorBlind(after.colorBlind).then(() => refreshActiveScene());
  }
  if (patch.locale !== undefined && patch.locale !== before.locale) {
    void loadLocale(after.locale).then(() => {
      document.title = t('app.title');
      ui.refresh();
      refreshActiveScene();
    });
  }
  // SY-02.3 settings follow the account (server-side copy via /v1/sync), City outfits and Clearer items included
  const body = settingsBody(patch);
  if (Object.keys(body).length) services.sync.enqueue(op.settings(body));
}

function refreshActiveScene(): void {
  const g = ctx.game;
  if (!g) return;
  for (const key of ['map', 'shop'] as const) {
    if (g.scene.isActive(key)) {
      const sc = g.scene.getScene(key);
      sc.scene.restart(sc.sys.settings.data as object);
    }
  }
  // gameplay re-skins on the next level to keep the round intact
}

setSpriteSource((id, box) => art.cssSprite(id, box));
// DOM sprites drawn with a fallback before their (lazy) group arrived re-render once it has
let spritesDirty = false;
art.onLoaded(() => {
  if (spritesDirty) return;
  spritesDirty = true;
  requestAnimationFrame(() => {
    spritesDirty = false;
    ui.refresh();
  });
});
setUiSound((name, step) => services.audio.play(name, step));
// browsers keep audio locked until a gesture; any tap on the overlay counts too. Every tap also
// wakes a context iOS left 'interrupted' (a call, Siri, an ad) and refused to resume without one
window.addEventListener('pointerdown', () => services.audio.unlock(), { capture: true });
// Escape (and the native shell's back button, via ui.back()) dismisses the top view
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && ui.back()) e.preventDefault();
});
mountOverlay(document.getElementById('ui')!, {
  ui,
  services,
  fast: () => ctx.flags.skipAnimations,
  onSettings: changeSettings,
  openSettings: () => void openView(ctx, 'settings'),
});
applySettings(store.state.settings);
void loadLocale(store.state.settings.locale).then(() => {
  document.title = t('app.title');
  ui.refresh();
});

// MO-01: contextual ads for children and without consent to personalised ads
function initAds(): void {
  const caps = services.privacy.caps();
  void services.ads.init(caps.personalizedAds, store.state.coppa);
}
initAds();
services.providers.consent.onChange(() => initAds());
// SY-02.1: the age question comes before the first ad (contextual-only ads for children)
services.ads.setBeforeFirstAd(async () => {
  if (!services.privacy.needsAge) return;
  await openView(ctx, 'privacyGate', { reason: 'ad' });
  initAds();
});
// a rewarded ad the network confirms late (AdMob SSV): "on its way", then "arrived"
services.ads.setNotice((n) => ui.toast(t(`ad.${n}`), n === 'arrived' ? 2200 : 3500));
services.ads.setLoading((on) => ui.setAdLoading(on));

// ---------------------------------------------------------------- Phaser
installDprScale();
const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: 'game',
  backgroundColor: '#ffd79e',
  scale: { mode: Phaser.Scale.RESIZE, autoCenter: Phaser.Scale.NO_CENTER },
  render: { antialias: true, powerPreference: 'high-performance' },
  input: { activePointers: 2, windowEvents: false },
  audio: { noAudio: true },
  banner: false,
  fps: { target: 60 },
  scene: [BootScene, MapScene, ShopScene, PlayScene],
});
ctx.game = game;
art.colorBlind = store.state.settings.colorBlind;

if (e2e || dev) {
  installHooks(ctx);
  (window as unknown as { __mpGame: Phaser.Game }).__mpGame = game;
}

// ---------------------------------------------------------------- native shell
// Android back, background/foreground, links into the running app, notification taps, the keyboard
native?.start(ctx, {
  confirmExit: () => Promise.resolve(window.confirm(t('app.exitConfirm'))),
  refreshConfig: loadRemoteConfig,
  handleLink,
  text: t,
});

// ---------------------------------------------------------------- lifecycle + sync
document.addEventListener('visibilitychange', () => {
  // no game sound in the background, and no burst of the notes a throttled timer missed on return
  services.audio.setHidden(document.hidden);
  if (document.hidden) {
    store.update((s) => (s.lastSeenAt = services.now()));
    store.flush();
    services.analytics.persist();
    void services.analytics.flush();
  } else {
    void services.sync.sync();
    void services.ads.settlePending();
  }
});
window.addEventListener('pagehide', () => {
  services.analytics.track('session_end', { durationMs: services.now() - bootNow });
  store.flush();
  services.analytics.persist();
});
window.addEventListener('online', () => {
  void services.sync.sync();
  void services.ads.settlePending();
});
setInterval(() => {
  if (!document.hidden) store.update((s) => (s.lastSeenAt = services.now()));
  void services.analytics.flush();
  if (store.state.outbox.some((o) => !o.held)) void services.sync.sync();
  if (!document.hidden) void services.ads.settlePending();
}, 20_000);

/** GET /v1/config into services.remote; true when it arrived (offline: the bundled defaults stay in force). */
function loadRemoteConfig(): Promise<boolean> {
  if (!services.api.enabled) return Promise.resolve(false);
  return services.api.config().then(
    (c) => {
      const r = c.remote as Partial<RemoteConfig>;
      Object.assign(services.remote, r, { flags: { ...services.remote.flags, ...(r.flags ?? {}), ...forcedFlags } });
      rememberRemote(services.kv, store.state.auth?.playerId ?? null, r); // the next start (offline too) plays by what the server resolved for this player
      trackExposures(services.analytics, services.remote);
      ui.refresh();
      return true;
    },
    (e: unknown) => {
      if (errorCode(e)) services.analytics.track('error', { where: 'config', reason: errorCode(e) });
      return false;
    },
  );
}

void services.sync.sync();
void loadRemoteConfig();
void services.ads.settlePending();

document.title = t('app.title');
void FEATURES; // manifests are eager (tiny); their views load on first use
